import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { MARCA, CONTATO } from "@/content/site";

const LINKS = [
  { label: "Início", to: "/", testid: "nav-link-inicio" },
  { label: "Imóveis", to: "/imoveis", testid: "nav-link-imoveis" },
  { label: "Sobre Nós", to: "/sobre", testid: "nav-link-sobre" },
  { label: "Contato", to: "/contato", testid: "nav-link-contato" },
];

export const Logo = ({ className = "" }) =>
  MARCA.logoUrl ? (
    <span className={`flex items-center gap-3 ${className}`}>
      <img src={MARCA.logoUrl} alt={MARCA.nomeCompleto} className="h-11 w-auto" />
      <span className="hidden font-serif text-lg tracking-tight text-[#F4F4F6] sm:inline">Imóveis</span>
    </span>
  ) : (
    <span className={`flex items-center gap-2.5 ${className}`}>
      <span className="flex h-10 w-10 items-center justify-center bg-brand font-serif text-lg font-bold tracking-tight text-white">
        NB
      </span>
      <span className="font-serif text-lg tracking-tight text-[#F4F4F6]">Imóveis</span>
    </span>
  );

const linkCls = ({ isActive }) =>
  `font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 hover:text-brand ${
    isActive ? "text-brand" : "text-stone-400"
  }`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wa = `https://wa.me/${CONTATO.whatsapp}?text=${encodeURIComponent("Olá! Vim pelo site da NB Imóveis e gostaria de mais informações.")}`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 backdrop-blur-xl transition-[background-color,border-color] duration-500 ${
        scrolled ? "bg-obsidian/90 border-b border-white/10" : "bg-obsidian/60 border-b border-white/5"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="nav-logo" aria-label={MARCA.nomeCompleto}>
          <Logo />
        </Link>

        <nav className="hidden items-center gap-9 lg:flex">
          {LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === "/"} data-testid={link.testid} className={linkCls}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-whatsapp"
            className="inline-flex items-center gap-2.5 border border-brand/60 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-brand transition-colors duration-300 hover:bg-brand hover:text-white"
          >
            <MessageCircle size={14} /> WhatsApp
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="text-stone-200 lg:hidden"
          aria-label="Abrir menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-obsidian/95 px-6 py-6 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-5">
            {LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`${link.testid}-mobile`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-mono text-xs uppercase tracking-[0.22em] ${isActive ? "text-brand" : "text-stone-300"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <a
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-cta-whatsapp-mobile"
              className="mt-2 flex items-center justify-center gap-2.5 border border-brand/60 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-brand"
            >
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
